// Floating Ko-fi "Support Us" overlay — Home screen only.
//
// Replaces the old self-hosted Ko-fi link button (Home.tsx) with Ko-fi's
// floating-chat overlay widget. We load the overlay script from
// storage.ko-fi.com and call `kofiWidgetOverlay.draw()` from our OWN bundled
// code rather than the inline <script> Ko-fi suggests, so the CSP keeps its
// `script-src` inline ban — only the ko-fi origins are allowlisted
// (netlify.toml / DECISIONS §G.1).
//
// "Floating" widgets append themselves to <body> and persist across client
// navigation, but the brief keeps the cook (and Preview / Countdown) calm and
// distraction-free, so the widget is scoped to the calm, non-cooking surfaces:
// the Home screen and the post-cook "Enjoy your meal, Chef!" complete screen.
// It is mounted there and hidden everywhere else. The overlay script exposes
// no teardown, so we hide its injected nodes via a body class + a CSS rule
// (styles.css) rather than removing them.

import { theme } from './design/theme';

const SCRIPT_SRC = 'https://storage.ko-fi.com/cdn/scripts/overlay-widget.js';
const SCRIPT_ID = 'kofi-overlay-widget';
const HIDDEN_CLASS = 'cc-kofi-hidden';

const KOFI_USERNAME = 'broadbaseai';
const DRAW_OPTS: Record<string, string> = {
  type: 'floating-chat',
  'floating-chat.donateButton.text': 'Support Us',
  'floating-chat.donateButton.background-color': theme.brand.kofi.bg,
  'floating-chat.donateButton.text-color': theme.brand.kofi.fg,
};

interface KofiWidgetOverlay {
  draw: (username: string, opts: Record<string, string>) => void;
}

declare global {
  interface Window {
    kofiWidgetOverlay?: KofiWidgetOverlay;
  }
}

// Draw at most once per page load — a second draw would add a duplicate
// floating button. Subsequent Home mounts just un-hide the existing one.
let widgetDrawn = false;

function drawOnce(): void {
  if (widgetDrawn) return;
  try {
    if (!window.kofiWidgetOverlay) return;
    window.kofiWidgetOverlay.draw(KOFI_USERNAME, DRAW_OPTS);
    widgetDrawn = true;
  } catch {
    // Calm: a donation widget that can't draw is never worth surfacing.
  }
}

function loadAndDraw(): void {
  if (window.kofiWidgetOverlay) {
    drawOnce();
    return;
  }
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    existing.addEventListener('load', drawOnce, { once: true });
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.addEventListener('load', drawOnce, { once: true });
  document.head.appendChild(script);
}

/**
 * Show the floating Ko-fi widget (loading + drawing it on first call) and
 * return a cleanup that hides it again. Designed to be returned straight from
 * a Home `useEffect`, so the widget is visible only while Home is mounted.
 * No-op outside the browser (SSR / tests without a DOM).
 */
export function showKofiOverlay(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  document.body.classList.remove(HIDDEN_CLASS);
  loadAndDraw();
  return () => {
    if (typeof document !== 'undefined') document.body.classList.add(HIDDEN_CLASS);
  };
}
